import useLocalization from "@/hooks/useLocalization";
import { Box, alpha, useTheme } from "@mui/material";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DashboardChartTooltipProps,
  DashboardChartMetrics,
  DashboardGraphScope,
} from "../types";
import {
  addBucketLabels,
  formatInteger,
  getAcceptanceRate,
} from "../utils";
import ChartCard from "./ChartCard";
import DashboardTooltip from "./DashboardTooltip";

type ReservationRequestsOverTimeChartProps = {
  graphScope: DashboardGraphScope;
  metrics: DashboardChartMetrics[];
};

type TooltipPayload = {
  color?: string;
  dataKey?: string;
  name?: string;
  payload?: ReservationRequestsChartMetric;
  value?: number | string;
};

type ReservationRequestsChartMetric = DashboardChartMetrics & {
  acceptanceRate: number;
  bucketLabel: string;
};

const getTotalRequests = (payload: TooltipPayload[]) =>
  payload.find((item) => item.payload?.reservations !== undefined)?.payload
    ?.reservations;

export default function ReservationRequestsOverTimeChart({
  graphScope,
  metrics,
}: ReservationRequestsOverTimeChartProps) {
  const theme = useTheme();
  const { t, adapterLocale } = useLocalization();
  const unansweredColor =
    theme.palette.mode === "dark"
      ? theme.palette.error.light
      : theme.palette.error.dark;
  const chartData = addBucketLabels(
    metrics.map((metric) => ({
      ...metric,
      acceptanceRate: Math.round(getAcceptanceRate(metric)),
    })),
    graphScope,
    adapterLocale,
  );
  const getStatusLabel = (name: string) => {
    switch (name) {
      case "accepted":
        return t("dashboard:accepted");
      case "rejected":
        return t("dashboard:rejected");
      case "pending":
        return t("dashboard:pending");
      case "unanswered":
        return t("dashboard:unanswered");
      case "acceptanceRate":
        return t("dashboard:acceptanceRate");
      default:
        return name;
    }
  };
  const formatTooltipValue = (item: TooltipPayload) => {
    if (item.dataKey === "acceptanceRate") {
      return `${formatInteger(Number(item.value))}%`;
    }

    return formatInteger(Number(item.value));
  };
  const renderTooltip = ({
    active,
    label,
    payload,
  }: DashboardChartTooltipProps) => {
    if (!active || !payload?.length) return null;

    const tooltipPayload = payload as TooltipPayload[];
    const totalRequests = getTotalRequests(tooltipPayload);

    return (
      <DashboardTooltip
        title={label}
        items={tooltipPayload.map((item) => ({
          color: item.color,
          label: getStatusLabel(String(item.dataKey ?? item.name)),
          value: formatTooltipValue(item),
        }))}
        footer={
          totalRequests !== undefined
            ? `${t("dashboard:totalRequests")}: ${formatInteger(totalRequests)}`
            : undefined
        }
      />
    );
  };

  return (
    <ChartCard title={t("dashboard:reservationRequestsOverTime")}>
      <Box sx={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 0, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.palette.divider}
            />
            <XAxis dataKey="bucketLabel" stroke={theme.palette.text.secondary} />
            <YAxis
              yAxisId="requests"
              tickFormatter={(value) => formatInteger(Number(value))}
              stroke={theme.palette.text.secondary}
              width={64}
            />
            <YAxis
              yAxisId="acceptanceRate"
              domain={[0, 100]}
              orientation="right"
              tickFormatter={(value) => `${formatInteger(Number(value))}%`}
              stroke={theme.palette.text.secondary}
              width={64}
            />
            <Tooltip
              content={renderTooltip}
              cursor={{
                fill: alpha(theme.palette.primary.main, 0.12),
              }}
            />
            <Legend />
            <Bar
              dataKey="accepted"
              name={t("dashboard:accepted")}
              stackId="status"
              yAxisId="requests"
              fill={theme.palette.success.main}
              isAnimationActive={false}
            />
            <Bar
              dataKey="rejected"
              name={t("dashboard:rejected")}
              stackId="status"
              yAxisId="requests"
              fill={theme.palette.error.main}
              isAnimationActive={false}
            />
            <Bar
              dataKey="unanswered"
              name={t("dashboard:unanswered")}
              stackId="status"
              yAxisId="requests"
              fill={unansweredColor}
              isAnimationActive={false}
            />
            <Bar
              dataKey="pending"
              name={t("dashboard:pending")}
              stackId="status"
              yAxisId="requests"
              fill={theme.palette.warning.main}
              isAnimationActive={false}
              radius={[4, 4, 0, 0]}
            />
            <Line
              type="monotone"
              dataKey="acceptanceRate"
              name={t("dashboard:acceptanceRate")}
              yAxisId="acceptanceRate"
              stroke={theme.palette.info.main}
              strokeWidth={2}
              dot={{ r: 3 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </ChartCard>
  );
}
